import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Stack,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Helmet } from 'react-helmet-async';
import Services from 'src/services/Services';
import { tokenService } from 'src/services';

export default function FaqPage() {
  const token = tokenService?.getUser()?.token;
  const userType = tokenService?.getUser()?.type;

  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [selectedTab, setSelectedTab] = useState('avm');

  useEffect(() => {
    setLoading(true);

    let queryType = '';
    if (userType === 'highadmin') {
      queryType = selectedTab; // 'avm' veya 'esnaf'
    } else {
      queryType = userType === 'esnaf' ? 'esnaf' : 'avm';
    }

    Services.GetFaq(token, queryType)
      .then((res) => {
        if (res.data && res.data.status) {
          setAllData(res.data.result);
          setFilteredData(res.data.result);
        } else {
          setAllData([]);
          setFilteredData([]);
        }
      })
      .catch((err) => {
        console.error(err);
        setAllData([]);
        setFilteredData([]);
      })
      .finally(() => {
        setLoading(false);
      });
      
  }, [token, userType, selectedTab]); 

  // Arama / Filtreleme İşlemi
  useEffect(() => {
    if (!searchText.trim()) {
      setFilteredData(allData);
    } else {
      const lowerSearch = searchText.toLowerCase();
      const filtered = allData.filter((item) => {
        const titleMatch = item.title && item.title.toLowerCase().includes(lowerSearch);
        const descMatch = item.description && item.description.toLowerCase().includes(lowerSearch);
        return titleMatch || descMatch;
      });
      setFilteredData(filtered);
    }
  }, [searchText, allData]);

  const handleAccordionChange = (panelId) => (event, isExpanded) => {
    setExpanded(isExpanded ? panelId : false);
  };

  const handleTabChange = (newTab) => {
    if (selectedTab !== newTab) {
      setSelectedTab(newTab);
      setSearchText(''); 
    }
  };

  if (loading) {
    return (
      <Stack height="50vh" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      <Helmet>
        <title>Sıkça Sorulan Sorular</title>
      </Helmet>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          Sıkça Sorulan Sorular (SSS)
        </Typography>

        {userType === 'highadmin' && (
          <Stack
            direction="row"
            sx={{
              width: 300,
              border: '1px solid #EAECF0',
              borderRadius: '12px',
              overflow: 'hidden',
              mb: 3,
            }}
          >
            <Box
              flex={1}
              textAlign="center"
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ cursor: 'pointer', p: '5px' }}
              onClick={() => handleTabChange('avm')}
            >
              <Box
                sx={{
                  fontWeight: selectedTab === 'avm' ? '600' : '500',
                  color: selectedTab === 'avm' ? '#3A4A70' : '#667085',
                  backgroundColor:
                    selectedTab === 'avm' ? '#D8E8FF' : 'transparent',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: '10px',
                  borderRadius: '6px',
                  transition: 'all 0.3s ease',
                  boxShadow:
                    selectedTab === 'avm'
                      ? '0px 1px 2px 0px #1018280F, 0px 1px 3px 0px #1018281A'
                      : 'none',
                }}
              >
                AVM
              </Box>
            </Box>
            <Box
              flex={1}
              textAlign="center"
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ cursor: 'pointer', p: '5px' }}
              onClick={() => handleTabChange('esnaf')}
            >
              <Box
                sx={{
                  fontWeight: selectedTab === 'esnaf' ? '600' : '500',
                  color: selectedTab === 'esnaf' ? '#3A4A70' : '#667085',
                  backgroundColor:
                    selectedTab === 'esnaf' ? '#D8E8FF' : 'transparent',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: '10px',
                  borderRadius: '6px',
                  transition: 'all 0.3s ease',
                  boxShadow:
                    selectedTab === 'esnaf'
                      ? '0px 1px 2px 0px #1018280F, 0px 1px 3px 0px #1018281A'
                      : 'none',
                }}
              >
                Esnaf
              </Box>
            </Box>
          </Stack>
        )}

        <TextField
          fullWidth
          label="Ara"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          variant="outlined"
          sx={{ 
            my: 2, 
            width: '100%', 
            maxWidth: '400px', 
            backgroundColor: 'white', 
            mb: 3 
          }}
        />

        {filteredData?.length === 0 ? (
          <Typography color="text.secondary" sx={{ mt: 2 }}>
             {searchText ? 'Arama kriterlerine uygun sonuç bulunamadı.' : 'Bu kategori için veri bulunamadı.'}
          </Typography>
        ) : (
          filteredData?.map((item, index) => (
            <Accordion
              key={item.id || index}
              expanded={expanded === (item.id || index)}
              onChange={handleAccordionChange(item.id || index)}
              sx={{
                borderColor: '#ccc',
                mb: 2,
                borderRadius: '8px !important',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                width: '100%',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${item.id}-content`}
                id={`panel${item.id}-header`}
                sx={{
                  backgroundColor:
                    expanded === (item.id || index) ? '#F3F4F6' : 'transparent',
                  borderBottomColor: '#ccc',
                  minHeight: '56px',
                  borderBottom: expanded === (item.id || index) ? '1px solid #ccc' : 'none',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {item.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <Typography
                   variant="body1"
                   sx={{
                    color: '#4B5563',
                    fontSize: 16,
                    lineHeight: 1.6,
                    whiteSpace: 'pre-line'
                  }}
                >
                  {item.description}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>
    </>
  );
}